import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Tabs, 
  Tab, 
  Card, 
  CardContent,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
  Button,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ToggleButton,
  ToggleButtonGroup,
  Slide
} from '@mui/material';
import { 
  Restaurant as RestaurantIcon,
  LocalFireDepartment as FireIcon,
  Nature as VeganIcon,
  Close as CloseIcon,
  ShoppingCart as CartIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ArrowForward as ArrowForwardIcon,
  Home as HomeIcon,
  ShoppingBag as ShoppingBagIcon,
  LocalShipping as DeliveryIcon
} from '@mui/icons-material';
import MenuService from '../../firebase/MenuService';

// Tema personalizado para el componente
const customTheme = {
  palette: {
    background: {
      main: '#000000', // Fondo negro
      paper: '#1a1a1a' // Fondo de tarjetas más claro
    },
    primary: {
      main: '#fa4616', // Rojo anaranjado
      dark: '#fa4616',
      light: '#fa4616'
    },
    text: {
      primary: '#FFFFFF', // Texto principal blanco
      secondary: '#CCCCCC' // Texto secundario gris claro
    }
  }
};

// 🆕 CONFIGURACIÓN DE TIPOS DE PEDIDO
const ORDER_TYPES = {
  local: {
    id: 'local',
    name: 'LOCAL',
    shortName: 'Local',
    icon: HomeIcon,
    color: 'primary',
    description: 'Consumo en el restaurante'
  },
  takeaway: {
    id: 'takeaway',
    name: 'PARA LLEVAR',
    shortName: 'Takeaway',
    icon: ShoppingBagIcon,
    color: 'secondary',
    description: 'Recoger en el restaurante'
  }
};

const MenuDesignComponent = ({ viewOnly = false, orderType: initialOrderType = 'local' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Estados
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [flippedProducts, setFlippedProducts] = useState(new Set());
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  
  // 🆕 NUEVO: Estado para tipo de pedido
  const [currentOrderType, setCurrentOrderType] = useState(initialOrderType);

  // 🆕 Computed: Filtrar productos y categorías según disponibilidad
  const availableProducts = allProducts.filter(product => {
    return product.active && 
           product.availability && 
           product.availability[currentOrderType];
  });

  const availableCategories = allCategories.filter(category => {
    // Solo mostrar categorías que tengan al menos un producto disponible
    const hasAvailableProducts = availableProducts.some(product => 
      product.categoryId === category.id
    );
    return category.active && hasAvailableProducts;
  });

  // Filtrar productos por categoría seleccionada
  const filteredProducts = availableProducts.filter(product => product.categoryId === selectedCategory);

  // Efecto para cargar datos
  useEffect(() => {
    loadMenuData();
  }, []);

  // 🆕 Efecto para manejar cambio de tipo de pedido
  useEffect(() => {
    // Cargar carrito específico del tipo de pedido
    if (!viewOnly) {
      const savedCart = localStorage.getItem(`cart_${currentOrderType}`);
      if (savedCart) {
        try {
          setCart(JSON.parse(savedCart));
        } catch (error) {
          console.error('Error parsing saved cart:', error);
          setCart([]);
        }
      } else {
        setCart([]);
      }
    }

    // Reset categoria seleccionada si no tiene productos disponibles
    if (selectedCategory !== availableCategories[0]?.id) {
      const hasProductsInSelectedCategory = availableProducts.some(
        product => product.categoryId === selectedCategory
      );
      
      if (!hasProductsInSelectedCategory && availableCategories.length > 0) {
        setSelectedCategory(availableCategories[0].id);
      }
    }
  }, [currentOrderType, availableProducts, availableCategories, selectedCategory, viewOnly]);

  // Guardar carrito en localStorage cuando cambie
  useEffect(() => {
    if (!viewOnly && cart.length >= 0) {
      localStorage.setItem(`cart_${currentOrderType}`, JSON.stringify(cart));
    }
  }, [cart, currentOrderType, viewOnly]);

  // Cargar datos del menú desde Firebase
  const loadMenuData = async () => {
    setLoading(true);
    try {
      // Cargar todos los datos sin filtrar inicialmente
      const menuData = await MenuService.getPublicMenu('local'); // Cargamos todos
      
      setAllCategories(menuData.categories);
      setAllProducts(menuData.products);
      
      setError(null);
    } catch (err) {
      console.error('Error loading menu data:', err);
      setError('Error al cargar los datos del menú. Por favor, inténtalo de nuevo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Manejar cambio de tipo de pedido
  const handleOrderTypeChange = (event, newOrderType) => {
    if (newOrderType !== null) {
      setCurrentOrderType(newOrderType);
      setSelectedCategory(0); // Reset a "todas las categorías"
      setFlippedProducts(new Set()); // Reset productos volteados
    }
  };

  // Manejar cambio de categoría
  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };
  
  // Toggle voltear producto
  const handleFlipProduct = (productId) => {
    const newFlipped = new Set(flippedProducts);
    if (newFlipped.has(productId)) {
      newFlipped.delete(productId);
    } else {
      newFlipped.add(productId);
    }
    setFlippedProducts(newFlipped);
  };
  
  // Abrir diálogo de detalle
  const handleOpenDetail = (product) => {
    setSelectedProduct(product);
    setDetailDialogOpen(true);
  };

  // 🆕 Obtener precio correcto según tipo de pedido
  const getProductPrice = (product) => {
    if (product.pricing && product.pricing[currentOrderType] !== undefined) {
      return parseFloat(product.pricing[currentOrderType]);
    }
    return parseFloat(product.price || 0);
  };

  // Añadir al carrito (actualizado con precio correcto)
  const addToCart = (product) => {
    if (viewOnly) return;
    
    const productWithCorrectPrice = {
      ...product,
      price: getProductPrice(product),
      orderType: currentOrderType
    };
    
    const existingItemIndex = cart.findIndex(item => item.id === product.id);
    
    if (existingItemIndex >= 0) {
      const updatedCart = [...cart];
      updatedCart[existingItemIndex].quantity += 1;
      setCart(updatedCart);
    } else {
      setCart([...cart, { ...productWithCorrectPrice, quantity: 1 }]);
    }
  };

  // Eliminar del carrito
  const removeFromCart = (productId) => {
    const existingItemIndex = cart.findIndex(item => item.id === productId);
    
    if (existingItemIndex >= 0) {
      const updatedCart = [...cart];
      if (updatedCart[existingItemIndex].quantity > 1) {
        updatedCart[existingItemIndex].quantity -= 1;
      } else {
        updatedCart.splice(existingItemIndex, 1);
      }
      setCart(updatedCart);
    }
  };

  // Calcular total del carrito
  const cartTotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);

  // Obtener nombre de categoría con icono para mostrar
  const getCategoryDisplayName = (category) => {
    const icon = category.icon || '📋';
    return `${icon} ${category.name}`;
  };

  // 🆕 Renderizar selector de tipo de pedido
  const renderOrderTypeSelector = () => (
    <Box sx={{ 
      mb: 3, 
      display: 'flex', 
      justifyContent: 'center',
      px: 2
    }}>
      <ToggleButtonGroup
        value={currentOrderType}
        exclusive
        onChange={handleOrderTypeChange}
        aria-label="tipo de pedido"
        sx={{
          display: 'flex',
          gap: 1,
          '& .MuiToggleButton-root': {
            border: `2px solid ${customTheme.palette.primary.main}`,
            borderRadius: '12px !important',
            color: customTheme.palette.text.secondary,
            backgroundColor: 'transparent',
            fontFamily: 'Arial, sans-serif',
            fontWeight: 'bold',
            fontSize: { xs: '0.8rem', sm: '0.9rem' },
            textTransform: 'none',
            px: { xs: 2, sm: 3 },
            py: 1.5,
            minWidth: { xs: 100, sm: 140 },
            '&.Mui-selected': {
              backgroundColor: customTheme.palette.primary.main,
              color: customTheme.palette.text.primary,
              '&:hover': {
                backgroundColor: customTheme.palette.primary.dark,
              }
            },
            '&:hover': {
              backgroundColor: `${customTheme.palette.primary.main}20`,
              borderColor: customTheme.palette.primary.light,
            }
          }
        }}
      >
        {Object.values(ORDER_TYPES).map((type) => {
          const IconComponent = type.icon;
          const productCount = availableProducts.filter(p => 
            p.availability && p.availability[type.id]
          ).length;
          
          return (
            <ToggleButton 
              key={type.id} 
              value={type.id}
              disabled={productCount === 0}
            >
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                alignItems: 'center',
                gap: 1
              }}>
                <IconComponent fontSize="small" />
                <Typography variant="body2" sx={{ 
                  fontWeight: 'bold'
                }}>
                  {type.name}
                </Typography>
              </Box>
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
    </Box>
  );

  // 🆕 Mostrar información del tipo de pedido actual
  const renderCurrentOrderTypeInfo = () => {
    const currentType = ORDER_TYPES[currentOrderType];
    if (!currentType) return null;

    return (
      <Fade in={true} timeout={500}>
        <Box sx={{ 
          textAlign: 'center', 
          mb: 2,
          px: 2
        }}>
          <Chip
            icon={<currentType.icon />}
            label={`${currentType.description} - ${filteredProducts.length} productos disponibles`}
            sx={{
              backgroundColor: customTheme.palette.primary.main,
              color: customTheme.palette.text.primary,
              fontFamily: 'Arial, sans-serif',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              py: 2
            }}
          />
        </Box>
      </Fade>
    );
  };

  // Si está cargando
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: customTheme.palette.background.main
      }}>
        <CircularProgress sx={{ color: customTheme.palette.primary.main }} />
      </Box>
    );
  }

  // Si hay error
  if (error) {
    return (
      <Box sx={{ 
        backgroundColor: customTheme.palette.background.main,
        minHeight: '100vh',
        py: 4
      }}>
        <Container maxWidth="lg">
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              backgroundColor: customTheme.palette.primary.main,
              color: customTheme.palette.text.primary
            }}
          >
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: customTheme.palette.background.main,
      minHeight: '100vh',
      pb: 4
    }}>
      {/* Header con logo y título */}
      <Box sx={{ 
        backgroundColor: customTheme.palette.background.main,
        py: 4,
        position: 'relative'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'center',
            mb: 2,
            position: 'relative',
            width: '100%'
          }}>
            {/* Logo */}
            <Box
              component="a"
              href="https://lowcountryboys.es/"
              target="_self"
              rel="noopener noreferrer"
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                textDecoration: 'none',
                cursor: 'pointer'
              }}
            >
              <Box
                component="img"
                src="/Logotipo.png"
                alt="Logo del Restaurante"
                sx={{ 
                  height: { xs: 80, md: 150 },
                  width: 'auto',
                  maxWidth: 400,
                  filter: 'brightness(1.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    filter: 'brightness(1.4)'
                  }
                }}
              />
            </Box>
            
            {/* Carrito posicionado absolutamente en la esquina derecha */}
            {!viewOnly && (
              <IconButton 
                onClick={() => setCartOpen(true)}
                sx={{ 
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: customTheme.palette.primary.main,
                  fontSize: '2rem'
                }}
              >
                <Badge badgeContent={cart.length} color="error">
                  <CartIcon fontSize="large" />
                </Badge>
              </IconButton>
            )}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* 🆕 SELECTOR DE TIPO DE PEDIDO */}
        {renderOrderTypeSelector()}

        {/* Navegación de categorías CON ICONOS - Solo categorías con productos disponibles */}
        {availableCategories.length > 0 ? (
          <Box sx={{ 
            mb: 4,
            borderBottom: `3px solid ${customTheme.palette.primary.main}`
          }}>
            <Tabs
              value={selectedCategory}
              onChange={handleCategoryChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{
                style: {
                  backgroundColor: customTheme.palette.primary.main,
                  height: '4px'
                }
              }}
              sx={{ 
                '& .MuiTab-root': {
                  fontFamily: 'Arial, sans-serif',
                  textTransform: 'uppercase',
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.2rem' },
                  color: customTheme.palette.text.secondary,
                  minHeight: 60,
                  px: { xs: 1, sm: 2 },
                  '&.Mui-selected': {
                    color: customTheme.palette.primary.main,
                  },
                  '&:hover': {
                    color: customTheme.palette.primary.light,
                  },
                  '& .MuiTab-iconWrapper': {
                    marginBottom: '0px !important'
                  }
                }
              }}
            >
              
              {availableCategories.map((category) => {
                const categoryProductCount = availableProducts.filter(p => 
                  p.categoryId === category.id
                ).length;
                
                return (
                  <Tab 
                    key={category.id} 
                    label={`${getCategoryDisplayName(category)} (${categoryProductCount})`}
                    value={category.id}
                    sx={{
                      '& .MuiTab-label': {
                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                        fontWeight: 'bold',
                        letterSpacing: '0.5px'
                      }
                    }}
                  />
                );
              })}
            </Tabs>
          </Box>
        ) : (
          <Paper sx={{ 
            p: 4, 
            textAlign: 'center',
            backgroundColor: customTheme.palette.background.paper,
            border: `2px solid ${customTheme.palette.primary.main}`,
            mb: 4
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: customTheme.palette.text.primary,
                fontFamily: 'Arial, sans-serif',
                mb: 2
              }}
            >
              NO HAY PRODUCTOS DISPONIBLES PARA {ORDER_TYPES[currentOrderType].name}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: customTheme.palette.text.secondary,
                fontFamily: 'Arial, sans-serif'
              }}
            >
              Prueba cambiando el tipo de pedido arriba
            </Typography>
          </Paper>
        )}

        {/* Grid de productos estilo tarjetas - Solo productos disponibles */}
        <Grid container spacing={3}>
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product.id}>
                <Slide direction="up" in={true} timeout={500}>
                  <Card 
                    sx={{ 
                      backgroundColor: customTheme.palette.background.paper,
                      border: `2px solid ${customTheme.palette.primary.main}`,
                      borderRadius: 3,
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      cursor: product.description ? 'pointer' : 'default',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 10px 25px rgba(255, 69, 0, 0.3)`,
                        borderColor: customTheme.palette.primary.light
                      }
                    }}
                    onClick={() => product.description && handleFlipProduct(product.id)}
                  >
                    {/* Contenedor de imagen con efecto flip */}
                    <Box sx={{ 
                      height: 350,
                      position: 'relative',
                      transformStyle: 'preserve-3d',
                      transition: 'transform 0.6s',
                      transform: flippedProducts.has(product.id) ? 'rotateY(180deg)' : 'rotateY(0deg)',
                      borderBottom: `2px solid ${customTheme.palette.primary.main}`
                    }}>
                      
                      {/* Frente - Imagen del producto */}
                      <Box sx={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        backfaceVisibility: 'hidden',
                        overflow: 'hidden'
                      }}>
                        <Box
                          component="img"
                          src={product.image || '/api/placeholder/400/300'}
                          alt={product.name}
                          sx={{ 
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </Box>

                      {/* Reverso - Descripción */}
                      <Box sx={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 3,
                        backgroundColor: customTheme.palette.primary.main
                      }}>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: customTheme.palette.text.primary,
                            textAlign: 'center',
                            fontFamily: 'Arial, sans-serif',
                            lineHeight: 1.4,
                            mb: 2
                          }}
                        >
                          {product.description}
                        </Typography>
                        
                        {!viewOnly && (
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              addToCart(product);
                            }}
                            sx={{
                              backgroundColor: customTheme.palette.text.primary,
                              color: customTheme.palette.primary.main,
                              fontFamily: 'Arial, sans-serif',
                              fontWeight: 'bold',
                              '&:hover': {
                                backgroundColor: customTheme.palette.background.paper
                              }
                            }}
                          >
                            AÑADIR
                          </Button>
                        )}
                      </Box>
                    </Box>
                    
                    <CardContent sx={{ p: 1 }}>
                      {/* Nombre del producto */}
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontFamily: 'Arial, sans-serif',
                          color: customTheme.palette.primary.main,
                          textTransform: 'uppercase',
                          letterSpacing: 1,
                          mb: 1,
                          textAlign: 'center'
                        }}
                      >
                        {product.name}
                      </Typography>

                      {/* 🆕 Precio según tipo de pedido seleccionado */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center',
                        mb: 2
                      }}>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontFamily: 'Arial, sans-serif',
                            color: customTheme.palette.text.primary,
                            backgroundColor: customTheme.palette.primary.main,
                            px: 2,
                            py: 0.5,
                            borderRadius: 2,
                            transform: 'rotate(-2deg)'
                          }}
                        >
                          {getProductPrice(product).toFixed(2)}€
                        </Typography>
                      </Box>

                      {/* Chips de características */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        gap: 1,
                        minHeight: 32
                      }}>
                        {product.isVegan && (
                          <Chip 
                            icon={<VeganIcon />} 
                            size="small" 
                            label="VEGANO" 
                            sx={{ 
                              backgroundColor: '#4CAF50', 
                              color: 'white',
                              fontFamily: 'Arial, sans-serif',
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                        {product.isSpicy && (
                          <Chip 
                            icon={<FireIcon />} 
                            size="small" 
                            label="PICANTE" 
                            sx={{ 
                              backgroundColor: customTheme.palette.primary.main, 
                              color: 'white',
                              fontFamily: 'Arial, sans-serif',
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                      </Box>

                      {/* Indicador de descripción disponible */}
                      {product.description && (
                        <Box sx={{ textAlign: 'center', mt: 1 }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: customTheme.palette.text.secondary,
                              fontStyle: 'italic',
                              fontSize: '0.8rem'
                            }}
                          >
                            Toca la imagen para ver descripción
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Slide>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Paper 
                sx={{ 
                  p: 4, 
                  textAlign: 'center',
                  backgroundColor: customTheme.palette.background.paper,
                  border: `2px solid ${customTheme.palette.primary.main}`
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: customTheme.palette.text.primary,
                    fontFamily: 'Arial, sans-serif',
                    mb: 1
                  }}
                >
                  NO HAY PRODUCTOS DISPONIBLES
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: customTheme.palette.text.secondary,
                    fontFamily: 'Arial, sans-serif'
                  }}
                >
                  {selectedCategory === 0 
                    ? `Para ${ORDER_TYPES[currentOrderType].description.toLowerCase()}`
                    : `En esta categoría para ${ORDER_TYPES[currentOrderType].description.toLowerCase()}`
                  }
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>

      {/* Drawer del Carrito */}
      {!viewOnly && (
        <Drawer
          anchor="right"
          open={cartOpen}
          onClose={() => setCartOpen(false)}
          PaperProps={{
            sx: {
              backgroundColor: customTheme.palette.background.paper,
              color: customTheme.palette.text.primary,
              width: { xs: '100vw', sm: 400 }
            }
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 3,
              borderBottom: `2px solid ${customTheme.palette.primary.main}`,
              pb: 2
            }}>
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontFamily: 'Arial, sans-serif',
                    color: customTheme.palette.primary.main
                  }}
                >
                  TU PEDIDO
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: customTheme.palette.text.secondary,
                    fontFamily: 'Arial, sans-serif'
                  }}
                >
                  {ORDER_TYPES[currentOrderType].description}
                </Typography>
              </Box>
              <IconButton 
                onClick={() => setCartOpen(false)}
                sx={{ color: customTheme.palette.primary.main }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            {cart.length > 0 ? (
              <>
                <List>
                  {cart.map((item) => (
                    <ListItem 
                      key={item.id}
                      sx={{ 
                        borderBottom: `1px solid ${customTheme.palette.primary.main}`,
                        mb: 1,
                        px: 0
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography sx={{ 
                            color: customTheme.palette.text.primary,
                            fontFamily: 'Arial, sans-serif',
                            fontSize: '0.9rem'
                          }}>
                            {item.name.toUpperCase()}
                          </Typography>
                        }
                        secondary={
                          <Typography sx={{ 
                            color: customTheme.palette.text.secondary,
                            fontSize: '0.8rem'
                          }}>
                            {parseFloat(item.price || 0).toFixed(2)}€
                          </Typography>
                        }
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton 
                          size="small"
                          onClick={() => removeFromCart(item.id)}
                          sx={{ 
                            color: customTheme.palette.primary.main,
                            fontSize: '1rem'
                          }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography 
                          sx={{ 
                            mx: 2, 
                            color: customTheme.palette.text.primary,
                            minWidth: '20px',
                            textAlign: 'center'
                          }}
                        >
                          {item.quantity}
                        </Typography>
                        <IconButton 
                          size="small"
                          onClick={() => addToCart(item)}
                          sx={{ 
                            color: customTheme.palette.primary.main,
                            fontSize: '1rem'
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
                
                <Divider sx={{ 
                  my: 2, 
                  borderColor: customTheme.palette.primary.main,
                  borderWidth: 2
                }} />
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  mb: 3
                }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontFamily: 'Arial, sans-serif',
                      color: customTheme.palette.text.primary
                    }}
                  >
                    TOTAL:
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontFamily: 'Arial, sans-serif',
                      color: customTheme.palette.primary.main
                    }}
                  >
                    {cartTotal.toFixed(2)}€
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  startIcon={<ArrowForwardIcon />}
                  sx={{
                    backgroundColor: customTheme.palette.primary.main,
                    color: customTheme.palette.text.primary,
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '1.1rem',
                    py: 2,
                    '&:hover': {
                      backgroundColor: customTheme.palette.primary.dark
                    }
                  }}
                >
                  CONTINUAR PEDIDO
                </Button>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: customTheme.palette.text.secondary,
                    fontFamily: 'Arial, sans-serif'
                  }}
                >
                  TU CARRITO ESTÁ VACÍO
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: customTheme.palette.text.secondary,
                    fontFamily: 'Arial, sans-serif',
                    mt: 1
                  }}
                >
                  Añade productos para {ORDER_TYPES[currentOrderType].description.toLowerCase()}
                </Typography>
              </Box>
            )}
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

export default MenuDesignComponent;